import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { ReactNode } from 'react';

import { Button, ButtonAction, ButtonSize } from '@/components/buttons/Button';
import CloseIcon from '@/components/icons/CloseIcon';
import SecondaryHeading from '@/components/typography/SecondaryHeading';
import accessIcon from '@/public/icons/access.svg';
import coffeeIcon from '@/public/icons/coffee.svg';
import globalAccessIcon from '@/public/icons/global-access.svg';
import guestIcon from '@/public/icons/guest.svg';
import meetingRoomIcon from '@/public/icons/meeting-room.svg';
import managerIcon from '@/public/icons/mxm.svg';
import phoneBoothIcon from '@/public/icons/phone-booth.svg';
import officeIcon from '@/public/icons/private-office.svg';
import wifiIcon from '@/public/icons/wifi.svg';

import clsx from 'clsx';

type CoworkingProductExplainerProps = {
  className?: string;
};

function CoworkingProductExplainer({
  className,
}: CoworkingProductExplainerProps) {
  const t = useTranslations('search.coworking-product-explainer');

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button
          className={className}
          size={ButtonSize.SMALL}
          action={ButtonAction.SECONDARY}
          data-testid="comparision-membership-btn"
        >
          <CoworkingProductExplainerIcon className="mr-2" />
          {t('btn-picker')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/[.4] z-20">
          <Dialog.Content className="bg-almond-99 top-0 md:top-[100px] max-md:h-dvh w-dvw md:w-fit md:max-h-[85dvh] mx-auto p-6 md:py-8 md:px-10 text-sm md:rounded-lg overflow-y-auto">
            <SecondaryHeading className="mb-5">{t('title')}</SecondaryHeading>
            <p className="mb-10 text-sm md:max-w-[650px]">
              {t.rich('desc', {
                b: (chunks) => <b>{chunks}</b>,
              })}
            </p>

            <div className="flex flex-col mb-5 md:gap-5 md:flex-row">
              <Product className="max-md:w-full">
                <OptionLabel className="bg-teal-99">
                  {t('day-pass.option')}
                </OptionLabel>
                <ProductTitle>{t('day-pass.title')}</ProductTitle>
                <ProductDesc>{t('day-pass.desc')}</ProductDesc>

                <FeatureGroup className="max-md:w-full">
                  <FeatureGroupTitle className="bg-teal-99">
                    {t('day-pass.features-title')}
                  </FeatureGroupTitle>
                  <FeatureItemWrapper>
                    <FeatureItem
                      icon={coffeeIcon}
                      altIcon={t('day-pass.coffee-icon-alt')}
                    >
                      {t('day-pass.coffee')}
                    </FeatureItem>
                    <FeatureItem
                      icon={wifiIcon}
                      altIcon={t('day-pass.wifi-icon-alt')}
                    >
                      {t('day-pass.wifi')}
                    </FeatureItem>
                    <FeatureItem
                      icon={phoneBoothIcon}
                      altIcon={t('day-pass.phone-booth-icon-alt')}
                    >
                      {t('day-pass.phone-booth')}
                    </FeatureItem>
                    <FeatureItem
                      icon={managerIcon}
                      altIcon={t('day-pass.manager-icon-alt')}
                    >
                      {t('day-pass.manager')}
                    </FeatureItem>
                  </FeatureItemWrapper>
                </FeatureGroup>
              </Product>

              <p className="mt-3 mb-10 text-xs md:hidden">{t('note')}</p>

              <Product className="max-md:w-full">
                <OptionLabel className="bg-honey-60">
                  {t('membership.option')}
                </OptionLabel>
                <ProductTitle>{t('membership.title')}</ProductTitle>
                <ProductDesc>{t('membership.desc')}</ProductDesc>

                <div className="flex flex-col gap-5 md:gap-2 md:flex-row">
                  <FeatureGroup className="max-md:w-full">
                    <FeatureGroupTitle className="bg-honey-99">
                      {t('membership.features-title')}
                    </FeatureGroupTitle>
                    <FeatureItemWrapper>
                      <FeatureItem
                        icon={accessIcon}
                        altIcon={t('membership.access-icon-alt')}
                      >
                        {t('membership.access')}
                      </FeatureItem>
                      <FeatureItem
                        icon={meetingRoomIcon}
                        altIcon={t('membership.meeting-room-icon-alt')}
                      >
                        {t('membership.meeting-room')}
                      </FeatureItem>
                      <FeatureItem
                        icon={officeIcon}
                        altIcon={t('membership.office-icon-alt')}
                      >
                        {t('membership.office')}
                      </FeatureItem>
                      <FeatureItem
                        icon={guestIcon}
                        altIcon={t('membership.guest-icon-alt')}
                      >
                        {t('membership.guest')}
                      </FeatureItem>
                    </FeatureItemWrapper>
                  </FeatureGroup>

                  <FeatureGroup className="max-md:w-full">
                    <FeatureGroupTitle className="bg-honey-90">
                      {t('membership.global-access-title')}
                    </FeatureGroupTitle>

                    <FeatureItem
                      icon={globalAccessIcon}
                      altIcon={t('membership.global-access-icon-alt')}
                    >
                      {t('membership.global-access-desc')}
                    </FeatureItem>
                  </FeatureGroup>
                </div>
              </Product>
            </div>

            <p className="text-xs max-md:hidden">{t('note')}</p>

            <Dialog.Close asChild>
              <button
                className="absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center"
                aria-label="Close"
                data-testid="close-btn"
              >
                <CloseIcon className="w-[12px] h-[12px]" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FeatureItemWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="grid px-3 gap-y-5 max-md:grid-cols-2 md:grid-cols-1">
      {children}
    </div>
  );
}

function FeatureItem(props: {
  icon: string;
  altIcon: string;
  children: ReactNode;
}) {
  const { icon, altIcon, children } = props;

  return (
    <p className="flex flex-col items-center justify-center flex-1 gap-1 px-1 whitespace-pre-line">
      <Image className="h-7" src={icon} alt={altIcon} aria-hidden="true" />
      {children}
    </p>
  );
}

function FeatureGroupTitle(props: { children: ReactNode; className?: string }) {
  const { children, className } = props;
  return (
    <h4
      className={clsx(
        'p-2 px-3 font-bold md:min-h-14 md:whitespace-pre-line',
        className
      )}
    >
      {children}
    </h4>
  );
}

function FeatureGroup(props: { children: ReactNode; className?: string }) {
  const { children, className } = props;

  return (
    <div
      className={clsx(
        'flex flex-col flex-grow gap-5 pb-2 overflow-hidden border rounded-lg w-fit border-grey-70',
        'max-md:border-none max-md:rounded-none',
        className
      )}
    >
      {children}
    </div>
  );
}

function Product(props: { children: ReactNode; className?: string }) {
  const { children, className } = props;
  return (
    <div
      className={clsx(
        'flex flex-col p-5 text-center bg-white border rounded-lg shadow w-fit border-grey-70',
        'max-md:w-full max-md:px-0',
        className
      )}
    >
      {children}
    </div>
  );
}
function OptionLabel(props: { children: ReactNode; className?: string }) {
  const { children, className } = props;

  return (
    <p
      className={clsx(
        'mb-3 px-2 mx-auto rounded w-fit mt-[-1.90rem] font-medium',
        className
      )}
    >
      {children}
    </p>
  );
}

function ProductTitle({ children }: { children: ReactNode }) {
  return <h3 className="text-[20px] font-bold  mb-1">{children}</h3>;
}

function ProductDesc({ children }: { children: ReactNode }) {
  return <p className="mb-5"> {children}</p>;
}

function CoworkingProductExplainerIcon(props: { className: string }) {
  const { className } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      fill="none"
      className={className}
    >
      <g className="fill-teal-40 group-hover:fill-white" clipPath="url(#a)">
        <path
          fillRule="evenodd"
          d="M18.334 2.75H3.667a1.839 1.839 0 0 0-1.833 1.833v12.834c0 1.008.825 1.833 1.833 1.833h14.666a1.839 1.839 0 0 0 1.834-1.833V4.583a1.839 1.839 0 0 0-1.834-1.833Zm0 14.667H3.667V4.583h14.667v12.834Z"
          clipRule="evenodd"
        />
        <path
          fillRule="evenodd"
          d="M17.793 9.552 16.49 8.25l-2.906 2.906-1.293-1.302L11 11.147l2.585 2.603 4.207-4.198Z"
          clipRule="evenodd"
        />
        <path d="M9.167 6.417H4.584V8.25h4.583V6.417ZM9.167 10.084H4.584v1.833h4.583v-1.833ZM9.167 13.75H4.584v1.833h4.583V13.75Z" />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M0 0h22v22H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default CoworkingProductExplainer;
